import React, { useEffect, useRef, useState } from "react";
import styles from "./ContactDrawer.module.css";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: Record<string, string>) => void;
};

const ContactDrawer = React.memo(function ContactDrawer({ open, onClose, onSubmit }: Props) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<null | "success" | "error">(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const drawerRef = useRef<HTMLDivElement | null>(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?\d{6,15}$/; // optional +, 6-15 digits (adjust if needed)

  useEffect(() => {
    // lock body scroll when open
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    // clear specific error while typing
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!form.firstName.trim()) newErrors.firstName = "This field is required";
    if (!form.lastName.trim()) newErrors.lastName = "This field is required";
    if (!form.message.trim()) newErrors.message = "This field is required";
    if (!form.email.trim()) newErrors.email = "This field is required";
    else if (!emailRegex.test(form.email)) newErrors.email = "Please enter a valid e-mail address";
    if (!form.phone.trim()) newErrors.phone = "This field is required";
    else if (!phoneRegex.test(form.phone)) newErrors.phone = "Please enter a valid phone number";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setServerError(null);

    // Replace with your Formspree form endpoint (create at https://formspree.io)
    const FORMSPREE_ENDPOINT = "https://formspree.io/f/xrbonjjw";

    // form is valid — call onSubmit or perform API request
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setSubmitStatus("success");
        // call optional callback (keep for local logging)
        if (onSubmit) onSubmit(form);
        // reset form
        setForm({ firstName: "", lastName: "", email: "", phone: "", message: "" });
        setErrors({});
        // close after small delay so user sees success
        setTimeout(() => {
          setIsSubmitting(false);
          setSubmitStatus(null);
          onClose();
        }, 900);
      } else {
        const payload = await res.json().catch(() => null);
        setServerError(payload?.error || "Failed to send message. Please try later.");
        setSubmitStatus("error");
        setIsSubmitting(false);
      }
    } catch (err) {
      setServerError("Network error. Please try again.");
      setSubmitStatus("error");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className={styles.overlay}
        aria-hidden={!open}
        style={{ pointerEvents: open ? "auto" : "none", opacity: open ? 0.7 : 0 }}
        onClick={onClose}
      />
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        className={`${styles.drawer} ${open ? styles.open : ""}`}
        aria-hidden={!open}
      >
        <header className={styles.header}>
          <button className={styles.closeBtn} aria-label="Close contact form" onClick={onClose}>
            ✕
          </button>
          <h2 className={styles.title}>Describe what you need done.</h2>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.row}>
            <input
              name="firstName"
              placeholder="First Name"
              className={`${styles.input} ${errors.firstName ? styles.errorInput : ""}`}
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? "err-firstName" : undefined}
              value={form.firstName}
              onChange={handleChange}
              autoComplete="given-name"
            />
            {errors.firstName && <div id="err-firstName" className={styles.errorText}>{errors.firstName}</div>}
            <input
              name="lastName"
              placeholder="Last Name"
              className={`${styles.input} ${errors.lastName ? styles.errorInput : ""}`}
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? "err-lastName" : undefined}
              value={form.lastName}
              onChange={handleChange}
              autoComplete="family-name"
            />
            {errors.lastName && <div id="err-lastName" className={styles.errorText}>{errors.lastName}</div>}
          </div>

          <input
            name="email"
            placeholder="Email"
            className={`${styles.input} ${errors.email ? styles.errorInput : ""}`}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            value={form.email}
            onChange={handleChange}
            type="email"
            autoComplete="email"
          />
          {errors.email && <div id="err-email" className={styles.errorText}>{errors.email}</div>}

          <input
            name="phone"
            placeholder="Phone Number"
            className={`${styles.input} ${errors.phone ? styles.errorInput : ""}`}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "err-phone" : undefined}
            value={form.phone}
            onChange={handleChange}
            type="tel"
            autoComplete="tel"
          />
          {errors.phone && <div id="err-phone" className={styles.errorText}>{errors.phone}</div>}

          <textarea
            name="message"
            placeholder="Project description: f.e. AI agent, web, mobile or desktop application"
            className={`${styles.textarea} ${errors.message ? styles.errorInput : ""}`}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "err-message" : undefined}
            value={form.message}
            onChange={handleChange}
            rows={5}
          />
          {errors.message && <div id="err-message" className={styles.errorText}>{errors.message}</div>}

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Submit"}
            </button>
          </div>
          {submitStatus === "success" && <div className={styles.successText}>Message sent — thank you!</div>}
          {submitStatus === "error" && serverError && <div className={styles.errorText}>{serverError}</div>}
        </form>
      </div>
    </>
  );
});

export default ContactDrawer;